import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { getAppUsage } from '../services/api';
import DateRangeFilter from '../components/DateRangeFilter';
import ActivityTimelineChart from '../components/Applications/ActivityTimelineChart';
import { PageHeader } from '../components/ui/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';

function ApplicationsPage() {
  const { workerId } = useParams();
  const workerIdNum = workerId ? parseInt(workerId, 10) : null;
  const [appUsage, setAppUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterParams, setFilterParams] = useState({
    startDate: null,
    endDate: null,
    hours: null,
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId, filterParams]);

  const loadData = async () => {
    try {
      setLoading(true);
      const wid = workerId ? parseInt(workerId, 10) : null;
      const usageRes = await getAppUsage(
        filterParams.hours,
        wid,
        filterParams.startDate,
        filterParams.endDate
      );
      setAppUsage(usageRes.data.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load application data:', err);
      setError('Failed to load application usage data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (params) => {
    setFilterParams(params);
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getTotalDuration = () => appUsage.reduce((sum, app) => sum + app.duration, 0);

  const getPercentage = (duration) => {
    const total = getTotalDuration();
    return total > 0 ? ((duration / total) * 100).toFixed(1) : 0;
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  const emptySummary = appUsage.length === 0;

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
      <PageHeader
        title="Application Usage"
        description="Timeline of focus and totals by application for the selected range."
      />

      <DateRangeFilter onFilterChange={handleFilterChange} defaultHours={null} />

      {workerIdNum != null && !Number.isNaN(workerIdNum) && (
        <ActivityTimelineChart workerId={workerIdNum} filterParams={filterParams} />
      )}

      {emptySummary ? (
        <Alert variant="info">
          <AlertDescription>No application usage data available yet.</AlertDescription>
        </Alert>
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Total time by application</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4">
              {appUsage.map((app, index) => (
                <div key={index} className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm font-medium text-foreground">
                      {app.application}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {formatDuration(app.duration)} ({getPercentage(app.duration)}%)
                    </span>
                  </div>
                  <Progress value={parseFloat(getPercentage(app.duration))} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default ApplicationsPage;
